import './CreateTeamModal.scss';

import {
  Box,
  Button,
  Dialog,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import DismissIcon from 'assets/images/iPrise/Dismiss.svg';
import { useFormik } from 'formik';
import { TeamUserRoles } from 'pages/iPriseV2/common/enum/TeamUserRoles';
import React, { useMemo, useState } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';
import { MASTER_ENDPOINT } from 'utils/Constant';

import InfiniteSearchSelect from '../../../common/components/InfiniteSearchSelect/InfiniteSearchSelect';
import StylishLoader from '../../../common/components/StylishLoader/StylishLoader';
import {
  CreateTeamFormData,
  createTeamValidationSchema,
  TeamCustomerMapping,
} from '../validations/createTeamValidation';

interface CreateTeamModalProps {
  onClose: () => void;
  onSubmit: (data: CreateTeamFormData) => Promise<void>;
  open: boolean;
  initialValues?: CreateTeamFormData;
  initialLabels?: {
    businessHead?: string;
    customers?: Array<{ label: string; value: string }>;
    jobSources?: Array<{ label: string; value: string }>;
    teamManager?: string;
    /** Per-job-source initial customer options for edit mode */
    customersByJobSource?: Record<
      string,
      Array<{ label: string; value: string }>
    >;
  };
  mode?: 'create' | 'edit';
  title?: string;
  subtitle?: string;
  submitButtonText?: string;
}

// Mock data - replace with actual API data
const teamTypes = [
  { label: 'Sales', value: 'Sales' },
  { label: 'Recruitment', value: 'Recruitment' },
  { label: 'Permanent', value: 'permanent' },
  { label: 'Contract', value: 'contract' },
];

// Helper to generate 30-minute intervals
const generateTimeIntervals = () => {
  const intervals = [];
  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += 30) {
      const hour = i % 12 || 12;
      const ampm = i < 12 ? 'AM' : 'PM';
      const minute = j === 0 ? '00' : '30';
      const label = `${String(hour).padStart(2, '0')}:${minute} ${ampm}`;
      const value = `${String(i).padStart(2, '0')}:${minute}:00`;
      intervals.push({ label, value });
    }
  }
  return intervals;
};

const timeIntervals = generateTimeIntervals();

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  onClose,
  onSubmit,
  open,
  initialValues,
  initialLabels,
  mode = 'create',
  title,
  subtitle,
  submitButtonText,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultInitialValues: CreateTeamFormData = {
    businessHeadId: '',
    customerIds: [] as string[],
    jobSourceIds: [] as string[],
    teamCode: '',
    teamCustomers: [] as TeamCustomerMapping[],
    teamManagerId: '',
    teamName: '',
    teamType: '',
    workingHoursEnd: '17:00:00',
    workingHoursStart: '08:00:00',
  };

  const formik = useFormik<CreateTeamFormData>({
    enableReinitialize: true,
    initialValues: initialValues || defaultInitialValues,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await onSubmit(values);
        formik.resetForm();
        onClose();
      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    validationSchema: createTeamValidationSchema,
  });

  // Fetch Business Heads
  const { data: businessHeadsResp, isLoading: isBHLoading } = useFetchList<any>(
    ['business-heads-list'],
    `${MASTER_ENDPOINT.Admins}?roleType=${TeamUserRoles.BUSINESS_HEAD}&status=true`,
    { enabled: open },
  );

  // Fetch Managers
  const { data: managersResp, isLoading: isManagersLoading } =
    useFetchList<any>(
      ['managers-list'],
      `${MASTER_ENDPOINT.Admins}?roleType=${TeamUserRoles.MANAGER}&status=true`,
      { enabled: open },
    );

  const { data: jobSourcesResp } = useFetchList<any>(
    ['team-job-sources-list'],
    `${MASTER_ENDPOINT.JobSources}?status=true`,
    { enabled: open },
  );

  const businessHeads = useMemo(() => {
    return (
      businessHeadsResp?.data?.map((user: any) => ({
        label: `${String(user.firstName)} ${String(user.lastName)}`,
        value: String(user.id),
      })) || []
    );
  }, [businessHeadsResp]);

  const managers = useMemo(() => {
    return (
      managersResp?.data?.map((user: any) => ({
        label: `${String(user.firstName)} ${String(user.lastName)}`,
        value: String(user.id),
      })) || []
    );
  }, [managersResp]);

  const jobSourceOptions = useMemo(() => {
    const apiOptions =
      jobSourcesResp?.data?.map((item: any) => ({
        label: String(item.name || item.jobSourceName || ''),
        value: String(item.id),
      })) || [];

    return [...apiOptions, ...(initialLabels?.jobSources || [])].filter(
      (option, index, self) =>
        option.value &&
        self.findIndex((item) => item.value === option.value) === index,
    );
  }, [initialLabels?.jobSources, jobSourcesResp]);

  /** The Beeline job source id among currently selected ones (if any) */
  const beelineJobSourceId = useMemo(() => {
    return (
      (formik.values.jobSourceIds || []).find((id) => {
        const label = jobSourceOptions.find((o) => o.value === id)?.label || '';
        return label.toLowerCase() === 'beeline';
      }) || null
    );
  }, [formik.values.jobSourceIds, jobSourceOptions]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string | string[]>,
    field: string,
  ) => {
    formik.setFieldValue(field, event.target.value);
  };

  /**
   * When job sources selection changes:
   * - Update jobSourceIds
   * - Clear teamCustomers when Beeline is removed
   */
  const handleJobSourcesChange = (newValue: string[]) => {
    // Check if Beeline is still in the new selection
    const beelineStillSelected = newValue.some((id) => {
      const label = jobSourceOptions.find((o) => o.value === id)?.label || '';
      return label.toLowerCase() === 'beeline';
    });

    formik.setFieldValue('jobSourceIds', newValue);
    if (!beelineStillSelected) {
      formik.setFieldValue('teamCustomers', []);
    }
  };

  /**
   * Update teamCustomers for a specific jobSourceId when the user
   * selects/deselects customers in that job source's multi-select.
   */
  const handleCustomersChangeForJobSource = (
    jobSourceId: string,
    selectedCustomerIds: string[],
  ) => {
    const existingMappings: TeamCustomerMapping[] = (
      formik.values.teamCustomers || []
    ).filter((tc: TeamCustomerMapping) => tc.jobSourceId !== jobSourceId);

    const newMappings: TeamCustomerMapping[] = selectedCustomerIds.map(
      (customerId) => ({ customerId, jobSourceId }),
    );

    formik.setFieldValue('teamCustomers', [
      ...existingMappings,
      ...newMappings,
    ]);
  };

  /** Returns the currently selected customerIds for a given jobSourceId */
  const getSelectedCustomersForJobSource = (jobSourceId: string): string[] => {
    return (formik.values.teamCustomers || [])
      .filter((tc: TeamCustomerMapping) => tc.jobSourceId === jobSourceId)
      .map((tc: TeamCustomerMapping) => tc.customerId);
  };

  /** Returns the initial options for a given jobSourceId (used in edit mode) */
  const getInitialOptionsForJobSource = (
    jobSourceId: string,
  ): Array<{ label: string; value: string }> => {
    // customersByJobSource from initialLabels (edit mode)
    if (initialLabels?.customersByJobSource?.[jobSourceId]) {
      return initialLabels.customersByJobSource[jobSourceId];
    }
    // Fallback: derive from generic customers list
    if (initialLabels?.customers) {
      const selectedIds = getSelectedCustomersForJobSource(jobSourceId);
      return initialLabels.customers.filter((c) =>
        selectedIds.includes(c.value),
      );
    }
    return [];
  };

  return (
    <Dialog
      className="create-team-modal"
      maxWidth="sm"
      open={open}
      onClose={handleClose}>
      <Box className="modal-header">
        <Typography className="modal-title">
          {title || (mode === 'create' ? 'Create New Team' : 'Edit Team')}
        </Typography>
        <Typography className="modal-subtitle">
          {subtitle || 'Set up a team for client-specific job routing.'}
        </Typography>
        <IconButton className="close-button" onClick={handleClose}>
          <img src={DismissIcon} alt="Close" />
        </IconButton>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box className="modal-content">
          {/* Team Name */}
          <Box className="form-group">
            <Typography className="form-label">Team Name</Typography>
            <TextField
              error={formik.touched.teamName && Boolean(formik.errors.teamName)}
              helperText={formik.touched.teamName && formik.errors.teamName}
              name="teamName"
              placeholder="Enter team name"
              value={formik.values.teamName}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
            />
          </Box>

          {/* Team Code and Type */}
          <Box className="form-row">
            <Box className="form-group">
              <Typography className="form-label">Team Code</Typography>
              <TextField
                error={
                  formik.touched.teamCode && Boolean(formik.errors.teamCode)
                }
                helperText={formik.touched.teamCode && formik.errors.teamCode}
                name="teamCode"
                placeholder="Enter team code"
                value={formik.values.teamCode}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </Box>

            <Box className="form-group">
              <Typography className="form-label">Type</Typography>
              <FormControl
                error={
                  formik.touched.teamType && Boolean(formik.errors.teamType)
                }>
                <Select
                  displayEmpty
                  name="teamType"
                  value={formik.values.teamType}
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleSelectChange(e, 'teamType')}
                  MenuProps={{ className: 'create-team-menu' }}>
                  <MenuItem disabled value="">
                    Select a type
                  </MenuItem>
                  {teamTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.teamType && formik.errors.teamType && (
                  <FormHelperText>{formik.errors.teamType}</FormHelperText>
                )}
              </FormControl>
            </Box>
          </Box>

          {/* Business Head and Manager */}
          <Box className="form-row">
            <Box className="form-group">
              <Typography className="form-label">Business Head</Typography>
              <FormControl
                error={
                  formik.touched.businessHeadId &&
                  Boolean(formik.errors.businessHeadId)
                }>
                <Select
                  displayEmpty
                  name="businessHeadId"
                  value={formik.values.businessHeadId}
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleSelectChange(e, 'businessHeadId')}
                  renderValue={(selected) => {
                    const label = selected
                      ? businessHeads.find((h) => h.value === selected)
                          ?.label ||
                        initialLabels?.businessHead ||
                        selected
                      : 'Select Business Head';
                    return (
                      <Typography
                        sx={{
                          color: selected ? '#242424' : '#707070',
                          fontFamily: 'Inter !important',
                          fontSize: '13px !important',
                          fontWeight: selected ? 500 : 400,
                          marginTop: '2px',
                        }}>
                        {label}
                      </Typography>
                    );
                  }}
                  MenuProps={{ className: 'create-team-menu' }}>
                  <MenuItem disabled value="">
                    Select Business Head
                  </MenuItem>
                  {isBHLoading ? (
                    <MenuItem
                      disabled
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        minHeight: 'auto',
                        p: 0,
                      }}>
                      <StylishLoader fullPage={false} />
                    </MenuItem>
                  ) : businessHeads.length === 0 ? (
                    <MenuItem disabled>
                      <Typography
                        sx={{
                          fontFamily: 'Inter !important',
                          fontSize: '13px',
                        }}>
                        No records found
                      </Typography>
                    </MenuItem>
                  ) : (
                    businessHeads.map((head) => (
                      <MenuItem key={head.value} value={head.value}>
                        {head.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formik.touched.businessHeadId &&
                  formik.errors.businessHeadId && (
                    <FormHelperText>
                      {formik.errors.businessHeadId}
                    </FormHelperText>
                  )}
              </FormControl>
            </Box>

            <Box className="form-group">
              <Typography className="form-label">Manager</Typography>
              <FormControl
                error={
                  formik.touched.teamManagerId &&
                  Boolean(formik.errors.teamManagerId)
                }>
                <Select
                  displayEmpty
                  name="teamManagerId"
                  value={formik.values.teamManagerId}
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleSelectChange(e, 'teamManagerId')}
                  renderValue={(selected) => {
                    const label = selected
                      ? managers.find((m) => m.value === selected)?.label ||
                        initialLabels?.teamManager ||
                        selected
                      : 'Select Team Manager';
                    return (
                      <Typography
                        sx={{
                          color: selected ? '#242424' : '#707070',
                          fontFamily: 'Inter !important',
                          fontSize: '13px !important',
                          fontWeight: selected ? 500 : 400,
                          marginTop: '2px',
                        }}>
                        {label}
                      </Typography>
                    );
                  }}
                  MenuProps={{ className: 'create-team-menu' }}>
                  <MenuItem disabled value="">
                    Select Team Manager
                  </MenuItem>
                  {isManagersLoading ? (
                    <MenuItem
                      disabled
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        minHeight: 'auto',
                        p: 0,
                      }}>
                      <StylishLoader fullPage={false} />
                    </MenuItem>
                  ) : managers.length === 0 ? (
                    <MenuItem disabled>
                      <Typography
                        sx={{
                          fontFamily: 'Inter !important',
                          fontSize: '13px',
                        }}>
                        No records found
                      </Typography>
                    </MenuItem>
                  ) : (
                    managers.map((manager) => (
                      <MenuItem key={manager.value} value={manager.value}>
                        {manager.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formik.touched.teamManagerId &&
                  formik.errors.teamManagerId && (
                    <FormHelperText>
                      {formik.errors.teamManagerId}
                    </FormHelperText>
                  )}
              </FormControl>
            </Box>
          </Box>

          {/* Working Hours - Select box for 30-min intervals */}
          <Box className="form-row">
            <Box className="form-group">
              <Typography className="form-label">
                Working Hours Start
              </Typography>
              <FormControl
                error={
                  formik.touched.workingHoursStart &&
                  Boolean(formik.errors.workingHoursStart)
                }>
                <Select
                  displayEmpty
                  name="workingHoursStart"
                  value={formik.values.workingHoursStart}
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleSelectChange(e, 'workingHoursStart')}
                  MenuProps={{ className: 'create-team-menu' }}>
                  <MenuItem disabled value="">
                    Select Start Time
                  </MenuItem>
                  {timeIntervals.map((time) => (
                    <MenuItem key={time.value} value={time.value}>
                      {time.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.workingHoursStart &&
                  formik.errors.workingHoursStart && (
                    <FormHelperText>
                      {formik.errors.workingHoursStart}
                    </FormHelperText>
                  )}
              </FormControl>
            </Box>

            <Box className="form-group">
              <Typography className="form-label">Working Hours End</Typography>
              <FormControl
                error={
                  formik.touched.workingHoursEnd &&
                  Boolean(formik.errors.workingHoursEnd)
                }>
                <Select
                  displayEmpty
                  name="workingHoursEnd"
                  value={formik.values.workingHoursEnd}
                  onBlur={formik.handleBlur}
                  onChange={(e) => handleSelectChange(e, 'workingHoursEnd')}
                  MenuProps={{ className: 'create-team-menu' }}>
                  <MenuItem disabled value="">
                    Select End Time
                  </MenuItem>
                  {timeIntervals.map((time) => (
                    <MenuItem key={time.value} value={time.value}>
                      {time.label}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.workingHoursEnd &&
                  formik.errors.workingHoursEnd && (
                    <FormHelperText>
                      {formik.errors.workingHoursEnd}
                    </FormHelperText>
                  )}
              </FormControl>
            </Box>
          </Box>

          {/* Job Sources */}
          <Box className="form-group">
            <Typography className="form-label">Job Source</Typography>
            <InfiniteSearchSelect
              multiple
              endpoint={MASTER_ENDPOINT.JobSources}
              label=""
              placeholder="Select Job Sources"
              queryKey="clients-infinite-list"
              value={formik.values.jobSourceIds}
              onChange={(newValue) => handleJobSourcesChange(newValue || [])}
              initialOptions={initialLabels?.jobSources}
              extraParams="status=true"
              error={
                formik.touched.jobSourceIds &&
                Boolean(formik.errors.jobSourceIds)
              }
              helperText={
                formik.touched.jobSourceIds &&
                (formik.errors.jobSourceIds as string)
              }
            />
          </Box>

          {/* ── Customer Mapping — Beeline only ──────────────────────────── */}
          {beelineJobSourceId && (
            <Box className="form-group">
              <Typography className="form-label">Customer</Typography>
              <InfiniteSearchSelect
                multiple
                endpoint={MASTER_ENDPOINT.CustomerBeeline}
                label=""
                placeholder="Select Customers"
                queryKey="beeline-customers-infinite-list"
                value={getSelectedCustomersForJobSource(beelineJobSourceId)}
                onChange={(newValue) =>
                  handleCustomersChangeForJobSource(
                    beelineJobSourceId,
                    newValue || [],
                  )
                }
                initialOptions={getInitialOptionsForJobSource(
                  beelineJobSourceId,
                )}
                mapResponse={(data) =>
                  (data?.data || []).map((item: any) => ({
                    label: String(
                      item.name || item.customerName || item.label || '',
                    ),
                    value: String(item.id || item.customerId || ''),
                  }))
                }
                extraParams="status=true"
                error={
                  !!formik.touched.teamCustomers &&
                  Boolean(formik.errors.teamCustomers)
                }
              />
            </Box>
          )}
        </Box>

        <Box className="modal-footer">
          <Button className="cancel-button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className="create-button"
            disabled={isSubmitting}
            type="submit"
            variant="contained">
            {isSubmitting ? (
              <StylishLoader
                fullPage={false}
                mode="inline"
                className={'button'}
              />
            ) : (
              submitButtonText ||
              (mode === 'create' ? 'Create Team' : 'Update Team')
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default CreateTeamModal;
